/* eslint-disable react/prop-types */
import SubmitContext from 'context/submit/submitContext'
import PageContext from 'context/page/pageContext'
import React, { useContext, useEffect, useState } from 'react'
import Switch from 'react-switch'
import { Col, Row, FormGroup, Input, Alert } from 'reactstrap'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import { packagingInitialState } from './constants'
import { useFormUtils } from 'views/shared/useFormUtils'
import { isNotNumericDecimal } from 'context/Utilities'
import PlantYogurtProduct from 'models/PlantYogurtProduct'

const PlantYogurtNutrition = () => {
  const submitContext = useContext(SubmitContext)
  const pageContext = useContext(PageContext)

  const { current, setCurrent, updating } = submitContext
  const { setScrollToTop } = pageContext

  const [packaging, setPackaging] = useState(packagingInitialState)

  const methods = useFormContext()

  const { setSwitch, onChange, asterisk } = useFormUtils()

  useEffect(() => {
    setScrollToTop(true)
  }, [])

  useEffect(() => {
    if (
      current?.category === packagingInitialState.category &&
      current?.subCategory === packagingInitialState.subCategory
    ) {
      const {
        VitaminD,
        Sugar,
        Calcium,
        Protein,
        SweetenerCertification,
        SugarCertification,
        CalciumCertification,
        ProteinCertification,
      } = current

      setPackaging({
        VitaminD,
        Sugar,
        Calcium,
        Protein,
        SweetenerCertification: SweetenerCertification ?? false,
        SugarCertification: SugarCertification ?? false,
        CalciumCertification: CalciumCertification ?? false,
        ProteinCertification: ProteinCertification ?? false,
      })
    } else if (!updating) {
      setCurrent(PlantYogurtProduct)
    }
  }, [current])

  const renderField = (
    name,
    label,
    maxLength,
    placeholder,
    min,
    pattern,
    message
  ) => (
    <FormGroup>
      <p>
        <strong>
          {label} {asterisk}
        </strong>
      </p>
      <Controller
        as={Input}
        type="number"
        step="0.01"
        name={name}
        placeholder={placeholder}
        control={methods.control}
        rules={{
          required: {
            value: true,
            message: `⚠ Please enter ${label}`,
          },
          maxLength: {
            value: maxLength,
            message: `⚠ ${label} exceeds ${maxLength} characters`,
          },
          min:
            min !== null
              ? {
                  value: min,
                  message: message,
                }
              : undefined,
          pattern: {
            value: pattern,
            message: '⚠ Maximum two decimal places allowed.',
          },
        }}
        onKeyDown={e => isNotNumericDecimal(e.key) && e.preventDefault()}
        onChange={([event]) => {
          onChange(event)
          return event
        }}
      />
      <ErrorMessage
        errors={methods.errors}
        name={name}
        as={<Alert color="danger" className="mt-3" />}
      />
    </FormGroup>
  )

  const renderSwitch = (name, label, errorMessage, defaultVal) => (
    <FormGroup>
      <p>
        <strong>
          {label} {asterisk}
        </strong>
      </p>
      <Controller
        as={Switch}
        name={name}
        id={name}
        control={methods.control}
        defaultValue={defaultVal}
        onChange={([checked, event, id]) => {
          setSwitch(checked, event, id)
          return checked
        }}
        rules={{ validate: value => value === true }}
      />
      <ErrorMessage
        errors={methods.errors}
        name={name}
        message={errorMessage}
        as={<Alert color="danger" className="mt-3" />}
      />
    </FormGroup>
  )

  return (
    <>
      <Row>
        <Col xs={6}>
          {renderField(
            'VitaminD',
            'Vitamin D (mcg) per 8 ounces',
            4,
            '',
            null,
            /^\d+(\.\d{1,2})?$/,
            ''
          )}
        </Col>
        <Col xs={6}>
          {renderSwitch(
            'SweetenerCertification',
            'I certify that this product does not contain artificial sweeteners, reduced-calorie sweeteners, or no-calorie sweeteners.',
            '⚠ You must certify that this product does not contain artificial sweeteners, reduced-calorie sweeteners, or no-calorie sweeteners.',
            packaging.SweetenerCertification
          )}
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          <FormGroup>
            <p>
              <strong>Added Sugar (g) per 8 ounces {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="Sugar"
              onKeyDown={e => isNotNumericDecimal(e.key) && e.preventDefault()}
              control={methods.control}
              placeholder={'Max 16g added sugar per 8 ounces'}
              rules={{
                required: '⚠ Please enter Added Sugar (g) per 8 ounces.',
                maxLength: {
                  value: 4,
                  message:
                    '⚠ Added Sugar (g) per 8 ounces exceeds 4 characters.',
                },
                max: {
                  value: 16,
                  message:
                    '⚠ Added Sugar (g) per 8 ounces exceeds the 16g maximum allowed.',
                },
                pattern: {
                  value: /^\d+(\.\d{1,2})?$/,
                  message: '⚠ Maximum two decimal places allowed.',
                },
              }}
              defaultValue={packaging.Size}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <ErrorMessage
              errors={methods.errors}
              name="Sugar"
              as={<Alert color="danger" className="mt-3" />}
            />
          </FormGroup>
        </Col>
        <Col xs={6}>
          {renderSwitch(
            'SugarCertification',
            'I certify that this product contains no more than the maximum 16g total added sugar allowed per 8 ounces.',
            '⚠ You must certify that this product contains no more than the maximum 16g total added sugar allowed per 8 ounces.',
            packaging.SugarCertification
          )}
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          {renderField(
            'Calcium',
            'Calcium (mg) per 8 ounces',
            6,
            'Min 250mg calcium per 8 ounces',
            250,
            /^\d+(\.\d{1,2})?$/,
            '⚠ Calcium (mg) per 8 ounces is below the required 250mg minimum'
          )}
        </Col>
        <Col xs={6}>
          {renderSwitch(
            'CalciumCertification',
            'I certify that this product contains at least 250mg calcium per 8 ounces.',
            '⚠ You must certify that this product contains no less than the required minimum 250mg total calcium per 8 ounces.',
            packaging.CalciumCertification
          )}
        </Col>
      </Row>
      <br />
      <Row>
        <Col xs={6}>
          {renderField(
            'Protein',
            'Protein (g) per 8 ounces',
            4,
            'Min 6.5g protein per 8 ounces',
            6.5,
            /^\d+(\.\d{1,2})?$/,
            '⚠ Protein (g) per 8 ounces is below the required 6.5g minimum'
          )}
        </Col>
        <Col xs={6}>
          {renderSwitch(
            'ProteinCertification',
            'I certify that this product contains at least 6.5g protein per 8 ounces.',
            '⚠ You must certify that this product contains no less than the required minimum 6.5g total protein per 8 ounces.',
            packaging.ProteinCertification
          )}
        </Col>
      </Row>
    </>
  )
}

export default PlantYogurtNutrition
